import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Tabs, DatePicker, PaginationProps, Tooltip, Button } from "antd";
import "./Attenance.scss";
import PresentTable from "./PresentTable";
import AddAttenanceModal from "./AddAttenanceModal";
import { useSelector } from "react-redux";
import { debounce, triggerNotification, useGetApiRequests, useNavigate } from "../../../common/CommonExport";
import dayjs from "dayjs";
import ModalOverallReportDownload from "../modal/ModalOverallReportDownload";
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

interface TabState {
  searchString: string;
  currentPage: number;
  pageSize: number;
  startDate: string;
  endDate: string;
  pickerValue: [dayjs.Dayjs, dayjs.Dayjs];
  presentList: any[];
  count?: number;
}

const Attendance = () => {
  const defaultStart = dayjs();
  const defaultEnd = dayjs();
  const [activeTabKey, setActiveTabKey] = useState("1");
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [isOverallReportModalOpen, setIsOverallReportModalOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const orgId = useSelector((state: any) => state?.organisationId?.id);
  const getEmployeeAttendance = useGetApiRequests("getAttendanceList", "POST");
  const getEsslAttendanceList = useGetApiRequests("getEsslAttendanceList", "POST");
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loader, setLoader] = useState(false);
  const isInitialMount = useRef(true);
  const clearTriggeredRef = useRef(false);

  const [biometricState, setBiometricState] = useState<TabState>({
    searchString: "",
    currentPage: 1,
    pageSize: 10,
    startDate: defaultStart.format("YYYY-MM-DD"),
    endDate: defaultEnd.format("YYYY-MM-DD"),
    pickerValue: [defaultStart, defaultEnd],
    presentList: [],
    count: 0,
  });

  const [geofenceState, setGeofenceState] = useState<TabState>({
    searchString: "",
    currentPage: 1,
    pageSize: 10,
    startDate: defaultStart.format("YYYY-MM-DD"),
    endDate: defaultEnd.format("YYYY-MM-DD"),
    pickerValue: [defaultStart, defaultEnd],
    presentList: [],
    count: 0,
  });

  const [biometricSearchInput, setBiometricSearchInput] = useState("");
  const [geofenceSearchInput, setGeofenceSearchInput] = useState("");

  const currentTabState = useMemo(() => (activeTabKey === "1" ? biometricState : geofenceState), [activeTabKey, biometricState, geofenceState]);

  const updateCurrentTabState = (updates: Partial<TabState>) => {
    if (activeTabKey === "1") {
      setBiometricState((prev) => ({ ...prev, ...updates }));
    } else {
      setGeofenceState((prev) => ({ ...prev, ...updates }));
    }
  };

  const getCurrentSearchInput = () => (activeTabKey === "1" ? biometricSearchInput : geofenceSearchInput);

  const updateCurrentSearchInput = (value: string) => {
    if (activeTabKey === "1") {
      setBiometricSearchInput(value);
    } else {
      setGeofenceSearchInput(value);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm: string, tabKey: string) => {
      if (tabKey === "1") {
        setBiometricState((prev) => ({ ...prev, searchString: searchTerm, currentPage: 1 }));
      } else {
        setGeofenceState((prev) => ({ ...prev, searchString: searchTerm, currentPage: 1 }));
      }
    }, 800),
    [],
  );

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    updateCurrentSearchInput(value);
    debouncedSearch(value, activeTabKey);
  };

  const handleStateAndEndDate = (dates: any) => {
    if (dates) {
      const [startDate, endDate] = dates;
      updateCurrentTabState({
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
        pickerValue: dates,
        currentPage: 1,
      });
    } else {
      const today = dayjs();
      updateCurrentTabState({
        startDate: today.format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
        pickerValue: [today, today],
        currentPage: 1,
      });
      clearTriggeredRef.current = true;
      setIsPickerOpen(false);
    }
  };

  const formatDates = (inputDate: any) => dayjs(inputDate).format("DD MMM, YYYY");
  const formatTime = (inputDateTime: any, isEssl?: boolean) => {
    if (!inputDateTime) return "-";

    if (isEssl) {
      return dayjs.utc(inputDateTime).add(5.5, "hour").format("h:mm A");
    } else {
      return dayjs.utc(inputDateTime).format("h:mm A");
    }
  };

  const formatWorkingHours = (decimalHours: number): string => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const getAttendanceList = useCallback(
    async (key: string, stateToUse: TabState) => {
      setLoader(true);
      const isEssl = key === "1";
      const payload: any = {
        searchAttendanceDto: {
          // employeeCode: stateToUse.searchString || "",
          employeeName: stateToUse.searchString || "",
          startDate: stateToUse.startDate,
          endDate: stateToUse.endDate,
          organisationId: orgId,
        },
        start: stateToUse.currentPage - 1,
        limit: stateToUse.pageSize,
        ...(isEssl ? {} : { sortBy: "employeeGeoAttendanceId" }),
        sortDirection: "DESC",
      };

      try {
        const response: any = isEssl ? await getEsslAttendanceList(payload) : await getEmployeeAttendance(payload);

        const employee = response?.data?.data || [];

        const count = isEssl ? response?.data?.totalResults : response?.data?.meta?.size;
        setTotalRecords(count || 0);

        const reformedPresentList = employee.map((item: any) => ({
          employeeCode: item?.employeeCode || "-",
          employeeName: item?.employeeName,
          date: formatDates(item?.attendanceDate),
          inTime: formatTime(item?.entryCheckIn, isEssl),
          outTime: item?.exitCheckOut && (!isEssl || item?.entryCheckIn !== item?.exitCheckOut) ? formatTime(item.exitCheckOut, isEssl) : "-",
          duration: item?.totalWorkingHours ? formatWorkingHours(item.totalWorkingHours) : "-",
          key: `${item?.employeeId}-${item?.attendanceDate}`,
        }));

        const setState = key === "1" ? setBiometricState : setGeofenceState;
        setState((prev) => ({ ...prev, presentList: reformedPresentList, count: count || 0 }));
      } catch (err: any) {
        triggerNotification("error", "", err?.message, "topRight");
        const setState = key === "1" ? setBiometricState : setGeofenceState;
        setState((prev) => ({ ...prev, presentList: [], count: 0 }));
      } finally {
        setLoader(false);
      }
    },
    [getEsslAttendanceList, getEmployeeAttendance, orgId],
  );
  const handleDownloadReport = async () => {
    try {
      const isEssl = activeTabKey === "1";
      const stateToUse = currentTabState;

      const payload: any = {
        searchAttendanceDto: {
          employeeName: stateToUse.searchString || "",
          startDate: stateToUse.startDate,
          endDate: stateToUse.endDate,
          organisationId: orgId,
        },
        start: 0,
        limit: totalRecords,
        ...(isEssl ? {} : { sortBy: "employeeGeoAttendanceId" }),
        sortDirection: "DESC",
      };

      const response: any = isEssl ? await getEsslAttendanceList(payload) : await getEmployeeAttendance(payload);

      const allRecords = response?.data?.data || [];

      const headers = ["Employee Code, Employee Name", "Date", "In Time", "Out Time", "Duration"];
      const csvRows = [headers.join(",")];

      allRecords.forEach((item: any) => {
        const row = [
          `"${item.employeeCode?.trim() || "-"}"`,
          `"${item.employeeName?.trim() || "-"}"`,
          `"${dayjs(item.attendanceDate).format("DD MMM, YYYY")}"`,
          `"${formatTime(item.entryCheckIn, isEssl)}"`,
          `"${item.exitCheckOut && (!isEssl || item.entryCheckIn !== item.exitCheckOut) ? formatTime(item.exitCheckOut, isEssl) : "-"}"`,
          `"${item.totalWorkingHours ? formatWorkingHours(item.totalWorkingHours) : "-"}"`,
        ];
        csvRows.push(row.join(","));
      });

      const csvContent = "\uFEFF" + csvRows.join("\n");
      const tabName = isEssl ? "Biometric" : "Geofence";
      const fileName = `Attendance_Report_${tabName}_${stateToUse.startDate}_to_${stateToUse.endDate}.csv`;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      triggerNotification("error", "", "Failed to download report", "topRight");
    }
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      getAttendanceList(activeTabKey, activeTabKey === "1" ? biometricState : geofenceState);
      return;
    }
    getAttendanceList(activeTabKey, currentTabState);
  }, [activeTabKey, currentTabState.searchString, currentTabState.startDate, currentTabState.endDate, currentTabState.currentPage, currentTabState.pageSize]);

  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
  };

  const handleRoutes = () => navigate("/home/dashboard");

  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, size) => {
    updateCurrentTabState({ currentPage: 1, pageSize: size });
  };

  const disabledDate = (current: dayjs.Dayjs) => current && current > dayjs().endOf("day");
  const handleClose = () => {
    setModalOpen(false);
  };
  return (
    <div className="page-container bg-customBg attenance">
      <div className="full-width-container mt-5">
        <div className="bg-white p-5">
          <div className="flex justify-between">
            <ul className="flex">
              <li className="text-[#878787] text-[16px] cursor-pointer hover:text-pink-500" onClick={handleRoutes}>
                Dashboard /
              </li>
              <li className="text-[16px] pl-1 text-[#ff4081] font-normal">Attendance</li>
            </ul>
            <div className="flex ml-auto gap-2">
              <div className="search-box mb-4 mr-5 flex">
                <input className="search-txt text-black search-box_hover" type="text" placeholder="Search" value={getCurrentSearchInput()} onChange={handleSearchInputChange} />
                <button className="search-btn">
                  <span className="material-symbols-outlined">search</span>
                </button>
              </div>
              <RangePicker
                className="mr-2 !h-[35px]"
                disabledDate={disabledDate}
                value={currentTabState.pickerValue}
                onChange={handleStateAndEndDate}
                open={isPickerOpen}
                onOpenChange={(open) => {
                  if (open) {
                    if (!clearTriggeredRef.current) {
                      setIsPickerOpen(true);
                    } else {
                      clearTriggeredRef.current = false;
                      setIsPickerOpen(false);
                    }
                  } else {
                    setIsPickerOpen(false);
                  }
                }}
                allowClear
                placeholder={["Start Date", "End Date"]}
              />
              <div>  {
                <Tooltip placement="bottom" title="Download Report">
                  <Button data-testid="downloadSpan" type="primary" shape="circle" className="cursor-pointer " size={"large"} onClick={handleDownloadReport} disabled={loader || totalRecords === 0}>
                    <span className="material-symbols-outlined">download</span>
                  </Button>
                </Tooltip>
              }</div>
              <div >
                <Tooltip placement="bottom" title="Download Attendance  Report">
                  <div className="flex justify-center ">
                    <Button
                      type="primary"
                      onClick={() => setIsOverallReportModalOpen(true)}
                      loading={false}
                      className="addButton px-6  flex items-center gap-2"
                    >

                      Overall Attendance
                      <span className="material-symbols-outlined" data-testid="download-icon">
                        download
                      </span>
                    </Button>


                  </div>
                </Tooltip>
              </div>

            </div>

          </div>

          <Tabs activeKey={activeTabKey} onChange={handleTabChange}>
            <TabPane key="1" tab="Biometric">
              <PresentTable
                presentList={biometricState.presentList}
                loader={loader}
                onShowSizeChange={onShowSizeChange}
                defaultCurrent={1}
                current={biometricState.currentPage}
                total={biometricState.count}
                pageSize={biometricState.pageSize}
                setCurrentPage={(page: number) => setBiometricState((prev) => ({ ...prev, currentPage: page }))}
              />
            </TabPane>
            <TabPane key="2" tab="Geofence">
              <PresentTable
                presentList={geofenceState.presentList}
                loader={loader}
                onShowSizeChange={onShowSizeChange}
                defaultCurrent={1}
                current={geofenceState.currentPage}
                total={geofenceState.count}
                pageSize={geofenceState.pageSize}
                setCurrentPage={(page: number) => setGeofenceState((prev) => ({ ...prev, currentPage: page }))}
              />
            </TabPane>
          </Tabs>
        </div>
      </div>
      {modalOpen && <AddAttenanceModal open={modalOpen} handleClose={handleClose} />}
      {isOverallReportModalOpen && (
        <ModalOverallReportDownload
          open={isOverallReportModalOpen}
          handleClose={() => setIsOverallReportModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Attendance;
